import { gql, useQuery } from '@apollo/client'
import { keyBy } from 'lodash'
import { formatReviewName } from '~/utilities/stringFormats'
import { useState, useEffect, useCallback } from 'react'
import { navigationCache } from '../localStorageCache'
import { env } from '~/env.ts'

const isProd = env.VITE_ENV === 'prod'

export interface NavigationDataResponse {
  me: {
    orgs: {
      id: number
      name: string
    }[]
  }
  projects: {
    id: number
    name: string
    orgId: number
    createdAt: string
    jobs: {
      id: number
      name: string
      revisedDesign: {
        id: number
        originalFilename: string
      }
    }[]
  }[]
}

const NAVIGATION_DATA_QUERY = gql`
  query NavigationDataQuery {
    projects {
      id
      name
      orgId
      createdAt
      jobs {
        id
        name
        revisedDesign {
          id
          originalFilename
        }
      }
    }
    me {
      orgs {
        id
        name
      }
    }
  }
`

export const useNavigationDataQuery = () => {
  const [cachedData, setCachedData] = useState<NavigationDataResponse | null>(null)
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true)
  const [cacheError, setCacheError] = useState<Error | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    const loadCachedData = () => {
      try {
        const cached = navigationCache.get<NavigationDataResponse>()
        if (cached) {
          setCachedData(cached)
          setCacheError(null)
          if (!isProd) {
            console.log('Loaded navigation data from cache')
          }
        }
      } catch (error) {
        if (!isProd) {
          console.error('Failed to load cached navigation data:', error)
        }
        setCacheError(error as Error)
      } finally {
        setIsLoadingFromCache(false)
      }
    }

    loadCachedData()
  }, [])

  const { data, loading, error, refetch } = useQuery<NavigationDataResponse>(
    NAVIGATION_DATA_QUERY,
    {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      skip: isLoadingFromCache,
      onCompleted: (newData) => {
        if (newData) {
          try {
            navigationCache.set(newData)
            setCachedData(newData)
            setCacheError(null)
            setIsRefreshing(false)
            if (!isProd) {
              console.log('Updated navigation data from network')
            }
          } catch (error) {
            console.error('Failed to update navigation cache:', error)
          }
        }
      },
      onError: (error) => {
        if (!isProd) {
          console.error('Navigation data query error:', error)
        }
        setCacheError(error)
        setIsRefreshing(false)
      }
    }
  )

  const refetchWithCache = useCallback(async () => {
    try {
      setIsRefreshing(true)
      const result = await refetch()
      if (result.data) {
        navigationCache.set(result.data)
        setCachedData(result.data)
        setCacheError(null)
      }
      return result
    } catch (error) {
      if (!isProd) {
        console.error('Failed to refetch navigation data:', error)
      }
      setCacheError(error as Error)
      throw error
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch])

  const refetchFromNetwork = useCallback(async () => {
    try {
      setIsRefreshing(true)
      const result = await refetch({
        fetchPolicy: 'network-only'
      })
      if (result.data) {
        navigationCache.set(result.data)
        setCachedData(result.data)
        setCacheError(null)
      }
      return result
    } catch (error) {
      if (!isProd) {
        console.error('Failed to refetch from network:', error)
      }
      setCacheError(error as Error)
      throw error
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch])

  const clearCache = useCallback(() => {
    try {
      navigationCache.remove()
      setCachedData(null)
      setCacheError(null)
    } catch (error) {
      if (!isProd) {
        console.error('Failed to clear cache:', error)
      }
    }
  }, [])

  const isCached = useCallback(() => {
    return navigationCache.hasValidCache()
  }, [])

  const getCacheAge = useCallback(() => {
    return navigationCache.getCacheAge()
  }, [])

  const currentData = cachedData ?? data
  const currentLoading = isLoadingFromCache || (loading && !cachedData)
  const currentError = cacheError ?? error

  return {
    navigationData: mapResponseToResult(currentData),
    loading: currentLoading,
    error: currentError,
    refetch: refetchWithCache,
    refetchFromNetwork,
    clearCache,
    isCached,
    getCacheAge,
    isUsingCache: !!cachedData && !data,
    isRefreshing,
    cacheAge: getCacheAge(),
    dataSource: cachedData ? (data ? 'cache-then-network' : 'cache-only') : 'network-only'
  }
}

const mapResponseToResult = (data: NavigationDataResponse | undefined) => {
  if (!data) return null
  if (!data.projects || !data.me) return null

  const clonedData = structuredClone(data)

  const projects = clonedData.projects.map((project) => {
    return {
      ...project,
      jobs: keyBy(
        project.jobs.map((job) => ({
          id: job.id,
          name: job.name ? job.name : formatReviewName(job.revisedDesign.originalFilename)
        })),
        (job) => job.id
      )
    }
  })

  const orgs = clonedData.me.orgs.map((org) => {
    return {
      ...org,
      projects: keyBy(
        projects.filter((project) => project.orgId === org.id),
        (project) => project.id
      )
    }
  })

  return {
    orgs: keyBy(orgs, (org) => org.id)
  }
}
